import React, { useState, useEffect, useRef } from "react";

import {
  Bookmark,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  X,
} from "lucide-react";

import { ScoreBubble } from "./common/ScoreBubble";
import { FullBriefingModal } from "./modals/FullBriefingModal";
import { SectionDetailModal } from "./modals/SectionDetailModal";
import {
  FLOATING_SELECTION_BAR_PANEL,
  useKpiSelectionBarDock,
} from "../../intelligence-briefing-library/components/hawkeye/KpiCompareSelectionStrip";
import {
  WatchlistSummaryAccount,
  WatchlistSummaryMatrixProps,
} from "../../Types/WatchlistSummary";

import "./style.scss";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
export const MATRIX_ACCOUNT_REVEAL_INTERVAL_MS = 900;
let lastAnimatedCycle = -1;

const getDefaultCollapsedBriefAccountIds = (
  accounts: WatchlistSummaryAccount[]
): Set<string> => {
  if (accounts.length <= 1) return new Set();
  return new Set(accounts.slice(1).map(account => account.accountId));
};

/** Staggered reveal for score bubbles + connector (left → right). */
export const MetricStaggerWrap: React.FC<{
  staggerIndex: number;
  children: React.ReactNode;
}> = ({ staggerIndex, children }) => (
  <div
    className="matrix-bubble-reveal-wrap"
    style={{ "--matrix-stagger": staggerIndex } as React.CSSProperties}
  >
    {children}
  </div>
);

export const WatchlistSummaryMatrix: React.FC<WatchlistSummaryMatrixProps> = ({
  summaryData,
  sections,
  selectedDate,
  animationCycle = 0,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  isLoading = false,
  watchlistName = "Watchlist",
  organizationName,
  userTimezone = "GMT",
  onSectionClick,
  onFullBriefingClick,
  onShareClick,
  onConfigureClick,
  onGenerateClick,
  sortConfig,
  onSort,
  enablePagination = true,
  pageSize: pageSizeProp = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  selectionPanelRef,
}) => {
  const [selectedAccount, setSelectedAccount] =
    useState<WatchlistSummaryAccount | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [isFullBriefingModalOpen, setIsFullBriefingModalOpen] = useState(false);
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeProp);
  const [visibleAccountCount, setVisibleAccountCount] = useState(0);
  /** Account IDs whose summary line is collapsed (default: first open, others closed). */
  const [collapsedBriefAccountIds, setCollapsedBriefAccountIds] = useState<
    Set<string>
  >(() => getDefaultCollapsedBriefAccountIds(summaryData));

  const isBriefLineExpanded = (accountId: string) =>
    !collapsedBriefAccountIds.has(accountId);

  const toggleBriefLine = (accountId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsedBriefAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  /** Stagger animation only on first paint; skip after any page change. */
  const prevPageForStaggerRef = useRef(currentPage);
  const [isStaggerSuppressed, setIsStaggerSuppressed] = useState(
    animationCycle <= lastAnimatedCycle
  );

  const orderedSummaryData = React.useMemo(() => {
    if (highlightedAccountIds.length === 0) return summaryData;

    const highlightedIds = new Set(highlightedAccountIds);
    const selectedAccounts = summaryData.filter(account =>
      highlightedIds.has(account.accountId)
    );
    const unselectedAccounts = summaryData.filter(
      account => !highlightedIds.has(account.accountId)
    );

    return [...selectedAccounts, ...unselectedAccounts];
  }, [summaryData, highlightedAccountIds]);

  const toggleAccountSelection = (accountId: string) => {
    if (!onHighlightedAccountIdsChange || !accountId) return;

    onHighlightedAccountIdsChange(
      highlightedAccountIds.includes(accountId)
        ? highlightedAccountIds.filter(id => id !== accountId)
        : [...highlightedAccountIds, accountId]
    );
  };

  const totalItems = orderedSummaryData.length;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = enablePagination
    ? orderedSummaryData.slice(startIndex, startIndex + pageSize)
    : orderedSummaryData;
  const dataToRender = paginatedData;

  useEffect(() => {
    setCurrentPage(1);
  }, [summaryData.length, pageSize]);

  useEffect(() => {
    if (highlightedAccountIds.length === 0) return;
    setCurrentPage(1);
  }, [highlightedAccountIds]);

  useEffect(() => {
    setCollapsedBriefAccountIds(
      getDefaultCollapsedBriefAccountIds(orderedSummaryData)
    );
  }, [orderedSummaryData]);

  useEffect(() => {
    prevPageForStaggerRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    // Do not replay stagger animation on page changes.
    if (prevPageForStaggerRef.current !== currentPage) {
      setIsStaggerSuppressed(true);
    }
  }, [currentPage]);

  useEffect(() => {
    // Replay stagger animation only for new API processing cycles.
    if (animationCycle > lastAnimatedCycle) {
      setIsStaggerSuppressed(false);
      setVisibleAccountCount(0);
    } else {
      setIsStaggerSuppressed(true);
    }
  }, [animationCycle]);

  useEffect(() => {
    setPageSize(pageSizeProp);
  }, [pageSizeProp]);

  useEffect(() => {
    if (isStaggerSuppressed) {
      setVisibleAccountCount(dataToRender.length);
      return;
    }

    if (dataToRender.length === 0) {
      setVisibleAccountCount(0);
      return;
    }

    setVisibleAccountCount(1);
    const revealInterval = window.setInterval(() => {
      setVisibleAccountCount(prev => {
        if (prev >= dataToRender.length) {
          window.clearInterval(revealInterval);
          setIsStaggerSuppressed(true);
          lastAnimatedCycle = Math.max(lastAnimatedCycle, animationCycle);
          return prev;
        }
        return prev + 1;
      });
    }, MATRIX_ACCOUNT_REVEAL_INTERVAL_MS);

    return () => window.clearInterval(revealInterval);
  }, [currentPage, dataToRender.length, isStaggerSuppressed]);

  // Filter and sort sections by display_order, only enabled sections, exclude summary
  const enabledSections = sections
    .filter(section => section.enabled && section.id !== "summary")
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const totalTableColumns = 2 + enabledSections.length + 1;
  const renderedAccounts = isStaggerSuppressed
    ? dataToRender
    : dataToRender.slice(0, visibleAccountCount);
  const isAccountRevealComplete =
    isStaggerSuppressed || visibleAccountCount >= dataToRender.length;

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateStr;
    }
  };

  const handleSectionClick = (accountName: string, sectionName: string) => {
    const account = orderedSummaryData.find(
      acc => acc.accountName === accountName
    );
    if (!account || !account.hasSelectedDateData) {
      console.warn("Cannot open section modal: account not found or no data", {
        accountName,
        sectionName,
        hasData: account?.hasSelectedDateData,
      });
      return;
    }

    if (onSectionClick) {
      onSectionClick(accountName, sectionName);
    } else {
      // Find account and section indices for navigation
      const validAccounts = orderedSummaryData.filter(
        acc => acc.hasSelectedDateData
      );
      const accountIdx = validAccounts.findIndex(
        acc => acc.accountName === accountName
      );

      // Calculate section index: -1 for overall, 0+ for regular sections
      let sectionIdx = -1; // Default to overall
      if (sectionName !== "overall") {
        sectionIdx = enabledSections.findIndex(sec => sec.id === sectionName);
        // If not found, default to -1 (overall)
        if (sectionIdx < 0) sectionIdx = -1;
      }

      // Opening section modal
      setCurrentAccountIndex(accountIdx >= 0 ? accountIdx : 0);
      setCurrentSectionIndex(sectionIdx);
      setSelectedAccount(account);
      setSelectedSection(sectionName);
      setIsSectionModalOpen(true);
    }
  };

  const handleFullBriefingClick = (account: WatchlistSummaryAccount) => {
    if (!account.hasSelectedDateData) {
      console.warn("Cannot open full briefing: no data", {
        accountName: account.accountName,
      });
      return;
    }

    if (onFullBriefingClick) {
      onFullBriefingClick(account);
    } else {
      // Opening full briefing modal
      setSelectedAccount(account);
      setIsFullBriefingModalOpen(true);
    }
  };

  const handleNavigate = (accountIndex: number, sectionIndex: number) => {
    const validAccounts = orderedSummaryData.filter(
      acc => acc.hasSelectedDateData
    );
    const account = validAccounts[accountIndex];

    if (!account) return;

    setCurrentAccountIndex(accountIndex);
    setCurrentSectionIndex(sectionIndex);
    setSelectedAccount(account);

    // Determine section name from index
    // sectionIndex: -1 = overall, 0+ = regular sections
    if (sectionIndex === -1) {
      setSelectedSection("overall");
    } else if (sectionIndex >= 0 && sectionIndex < enabledSections.length) {
      const section = enabledSections[sectionIndex];
      setSelectedSection(section?.id || null);
    }
  };

  const handleShareClick = (account: WatchlistSummaryAccount) => {
    if (onShareClick) {
      onShareClick(account);
    }
  };

  const renderSummaryTable = (
    rowsSource: WatchlistSummaryAccount[],
    accountCountLabel: string,
    animateAccountReveal: boolean
  ) => (
    <div className="watchlist-summary-card">
      <div className="watchlist-summary-card-content">
        <div className="table-container">
          <table className="summary-table">
            <thead>
              <tr>
                <th
                  className="header-cell account-header"
                  onClick={() => onSort && onSort("account")}
                  style={{ cursor: onSort ? "pointer" : "default" }}
                >
                  <div className="header-content">
                    <span>Account</span>
                    <span className="account-count">{accountCountLabel}</span>
                    {sortConfig?.field === "account" && (
                      <span
                        className={`sort-icon ${sortConfig.direction}`}
                      ></span>
                    )}
                  </div>
                </th>
                <th
                  className="header-cell overall-header"
                  onClick={() => onSort && onSort("overall")}
                  style={{ cursor: onSort ? "pointer" : "default" }}
                >
                  <div className="header-content">
                    <span>Overall</span>
                    {sortConfig?.field === "overall" && (
                      <span
                        className={`sort-icon ${sortConfig.direction}`}
                      ></span>
                    )}
                  </div>
                </th>
                {enabledSections.map(section => (
                  <th
                    key={section.id}
                    className="header-cell section-header"
                    onClick={() => onSort && onSort(section.id)}
                    style={{ cursor: onSort ? "pointer" : "default" }}
                  >
                    <div className="header-content">
                      <span>{section.name}</span>
                      {sortConfig?.field === section.id && (
                        <span
                          className={`sort-icon ${sortConfig.direction}`}
                        ></span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="header-cell actions-header">
                  <span></span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsSource.map((account, accountIndex) => {
                const hasSelectedDateData = account.hasSelectedDateData;
                const overallData = account.overallData;
                const sectionsData = account.sectionsData || [];
                const briefLine = account.accountBriefLine?.trim();
                const rowToneClass = !hasSelectedDateData ? "no-data-row" : "";
                const shouldAnimateAccountReveal =
                  animateAccountReveal && !isStaggerSuppressed;
                const accountRevealRowClass = shouldAnimateAccountReveal
                  ? "matrix-account-row-reveal"
                  : "";
                const accountBriefRevealClass = shouldAnimateAccountReveal
                  ? "matrix-account-brief-reveal"
                  : "";
                const accountBriefLineAnimationClass =
                  shouldAnimateAccountReveal ? "ai-generating-text" : "";
                const accountRevealStyle = shouldAnimateAccountReveal
                  ? ({
                      "--account-stagger": accountIndex,
                    } as React.CSSProperties)
                  : undefined;

                return (
                  <React.Fragment key={account.accountId}>
                    <tr
                      className={`table-row ${rowToneClass} ${accountRevealRowClass}`}
                      style={accountRevealStyle}
                    >
                      <td
                        className={`account-cell ${hasSelectedDateData ? "clickable" : ""}`}
                        onClick={() =>
                          hasSelectedDateData &&
                          handleSectionClick(account.accountName, "overall")
                        }
                      >
                        <div className="account-info">
                          <label
                            className="account-select-checkbox"
                            onClick={event => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              aria-label={`Select ${account.accountName}`}
                              checked={highlightedAccountIds.includes(
                                account.accountId
                              )}
                              onChange={() =>
                                toggleAccountSelection(account.accountId)
                              }
                              className="account-select-checkbox__input"
                            />
                            <span
                              className="account-select-checkbox__control"
                              aria-hidden="true"
                            >
                              <svg viewBox="0 0 16 16">
                                <path
                                  d="M3.5 8.5L6.3 11.3L12.5 5.1"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          </label>
                          {briefLine ? (
                            <button
                              type="button"
                              className="account-brief-toggle"
                              onClick={e =>
                                toggleBriefLine(account.accountId, e)
                              }
                              aria-expanded={isBriefLineExpanded(
                                account.accountId
                              )}
                              aria-controls={`account-brief-${account.accountId}`}
                              title={
                                isBriefLineExpanded(account.accountId)
                                  ? "Hide summary"
                                  : "Show summary"
                              }
                            >
                              {isBriefLineExpanded(account.accountId) ? (
                                <ChevronDown
                                  className="account-brief-toggle__icon"
                                  aria-hidden
                                />
                              ) : (
                                <ChevronRight
                                  className="account-brief-toggle__icon"
                                  aria-hidden
                                />
                              )}
                            </button>
                          ) : null}
                          <span
                            className={`account-icon ${!hasSelectedDateData ? "no-data" : ""}`}
                          >
                            {hasSelectedDateData ? "🏢" : "🏢"}
                          </span>
                          <div className="account-info-text">
                            <span className="account-name">
                              {account.accountName}
                            </span>
                          </div>
                        </div>
                      </td>

                      {!hasSelectedDateData ? (
                        <>
                          <td
                            className="data-cell overall-cell"
                            style={{ color: "#94a3b8", textAlign: "center" }}
                          >
                            &nbsp;
                          </td>
                          <td
                            colSpan={enabledSections.length + 1}
                            className="data-cell"
                            style={{
                              textAlign: "left",
                              paddingLeft: "8px",
                              color: "#94a3b8",
                            }}
                          >
                            No briefing found for this account for{" "}
                            {formatDate(selectedDate)} {userTimezone}{" "}
                          </td>
                        </>
                      ) : (
                        <>
                          <td
                            className={`data-cell overall-cell matrix-stagger-metric ${overallData ? "clickable" : ""}`}
                            onClick={() =>
                              overallData &&
                              handleSectionClick(account.accountName, "overall")
                            }
                            data-account-name={account.accountName}
                            data-section-name="overall"
                            style={
                              { "--matrix-stagger": 0 } as React.CSSProperties
                            }
                          >
                            <MetricStaggerWrap staggerIndex={0}>
                              {overallData ? (
                                <ScoreBubble
                                  healthFlag={overallData.overallHealthFlag}
                                  relevanceFlag={
                                    overallData.overallRelevanceFlag
                                  }
                                  healthScore={overallData.overallHealthScore}
                                  relevanceScore={
                                    overallData.overallRelevanceScore
                                  }
                                />
                              ) : (
                                <div
                                  style={{
                                    color: "#94a3b8",
                                    textAlign: "center",
                                    fontSize: "12px",
                                    padding: "4px",
                                  }}
                                >
                                  Summary N/A
                                </div>
                              )}
                            </MetricStaggerWrap>
                          </td>

                          {enabledSections.map((section, sectionIndex) => {
                            const sectionData = sectionsData.find(
                              s => s.section_name === section.id
                            );
                            const staggerIndex = sectionIndex + 1;
                            return (
                              <td
                                key={section.id}
                                className={`data-cell matrix-stagger-metric ${sectionData ? "clickable" : ""}`}
                                onClick={() =>
                                  sectionData &&
                                  handleSectionClick(
                                    account.accountName,
                                    section.id
                                  )
                                }
                                data-account-name={account.accountName}
                                data-section-name={section.id}
                                style={
                                  {
                                    "--matrix-stagger": staggerIndex,
                                  } as React.CSSProperties
                                }
                              >
                                <MetricStaggerWrap staggerIndex={staggerIndex}>
                                  {sectionData ? (
                                    <ScoreBubble
                                      healthFlag={sectionData.sectionHealthFlag}
                                      relevanceFlag={
                                        sectionData.sectionRelevanceFlag
                                      }
                                      healthScore={
                                        sectionData.sectionHealthScore
                                      }
                                      relevanceScore={
                                        sectionData.sectionRelevanceScore
                                      }
                                    />
                                  ) : (
                                    <div style={{ color: "#94a3b8" }}>
                                      &nbsp;
                                    </div>
                                  )}
                                </MetricStaggerWrap>
                              </td>
                            );
                          })}

                          <td
                            className="actions-cell matrix-actions-stagger"
                            style={
                              {
                                "--matrix-stagger": enabledSections.length + 1,
                              } as React.CSSProperties
                            }
                          >
                            <div className="actions-buttons">
                              <button
                                className="nav-btn"
                                onClick={() => handleFullBriefingClick(account)}
                                title="Full Briefing"
                              >
                                📋
                              </button>
                              <button
                                className="nav-btn"
                                onClick={() => handleShareClick(account)}
                                title="Share Briefing"
                              >
                                📤
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                    {briefLine && isBriefLineExpanded(account.accountId) ? (
                      <tr
                        id={`account-brief-${account.accountId}`}
                        className={`table-row account-brief-row ${rowToneClass} ${accountBriefRevealClass}`}
                        style={accountRevealStyle}
                        aria-label="Account summary"
                      >
                        <td
                          colSpan={totalTableColumns}
                          className="account-brief-line-cell"
                        >
                          <p
                            className={`account-brief-line account-brief-line--full ${accountBriefLineAnimationClass}`}
                            title={briefLine}
                          >
                            {briefLine}
                          </p>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="watchlist-summary-matrix loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const matrixRootClassName = [
    "watchlist-summary-matrix",
    isStaggerSuppressed ? "matrix-stagger-suppressed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const selectionBarDock = useKpiSelectionBarDock(
    selectionPanelRef ?? { current: null },
    Boolean(selectionPanelRef && highlightedAccountIds.length > 0)
  );

  const selectedAccounts = React.useMemo(
    () =>
      orderedSummaryData.filter(account =>
        highlightedAccountIds.includes(account.accountId)
      ),
    [orderedSummaryData, highlightedAccountIds]
  );

  const selectedAccountOverflowCount = Math.max(0, selectedAccounts.length - 8);
  const selectionStripVisibleAccounts = selectedAccounts.slice(0, 8);

  const accountInitials = (name: string) =>
    (name || "")
      .split(" ")
      .filter(Boolean)
      .map(part => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const renderPaginationControls = (
    totalForSlice: number,
    page: number,
    onPageChange: (nextPage: number) => void,
    options?: { showRowsPerPage?: boolean }
  ) => {
    if (!enablePagination || totalForSlice <= 0) return null;
    const showRowsPerPage = options?.showRowsPerPage !== false;
    const totalPagesLocal = Math.ceil(totalForSlice / pageSize) || 1;
    const startIdx = (page - 1) * pageSize;

    return (
      <div className="watchlist-summary-pagination">
        <div className="watchlist-summary-pagination__inner">
          {showRowsPerPage && totalForSlice > Math.min(...pageSizeOptions) && (
            <div className="watchlist-summary-pagination__size">
              <span className="watchlist-summary-pagination__label">Show</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="watchlist-summary-pagination__select"
                aria-label="Rows per page"
              >
                {pageSizeOptions.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="watchlist-summary-pagination__label">
                entries
              </span>
            </div>
          )}
          <div className="watchlist-summary-pagination__info">
            Showing <strong>{startIdx + 1}</strong> to{" "}
            <strong>{Math.min(startIdx + pageSize, totalForSlice)}</strong> of{" "}
            <strong>{totalForSlice}</strong> results
          </div>
          <div className="watchlist-summary-pagination__controls">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="watchlist-summary-pagination__btn"
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="watchlist-summary-pagination__page">
              Page <strong>{page}</strong> of <strong>{totalPagesLocal}</strong>
            </span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPagesLocal, page + 1))}
              disabled={page >= totalPagesLocal}
              className="watchlist-summary-pagination__btn"
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    );
  };

  const shouldReserveFloatingBarSpace =
    selectedAccounts.length > 0 && Boolean(selectionBarDock);

  return (
    <div
      className={matrixRootClassName}
      style={{
        paddingBottom: shouldReserveFloatingBarSpace ? "6.5rem" : undefined,
      }}
    >
      {renderSummaryTable(
        renderedAccounts,
        enablePagination && totalItems > pageSize
          ? `(${startIndex + 1}-${Math.min(startIndex + pageSize, totalItems)} of ${totalItems})`
          : `(${totalItems} total)`,
        !isStaggerSuppressed
      )}

      {enablePagination &&
        totalItems > 0 &&
        isAccountRevealComplete &&
        renderPaginationControls(totalItems, currentPage, setCurrentPage, {
          showRowsPerPage: true,
        })}

      {selectedAccounts.length > 0 && selectionBarDock ? (
        <div
          className="pointer-events-none fixed z-20"
          style={{
            left: selectionBarDock.left,
            width: selectionBarDock.width,
            bottom: selectionBarDock.bottom,
          }}
        >
          <div className={FLOATING_SELECTION_BAR_PANEL}>
            <ShoppingBag
              className="h-5 w-5 shrink-0 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {selectionStripVisibleAccounts.map(account => (
                <button
                  key={account.accountId}
                  type="button"
                  onClick={() => toggleAccountSelection(account.accountId)}
                  className="group relative shrink-0"
                  title={`Remove ${account.accountName} from selection`}
                  aria-label={`Remove ${account.accountName} from selection`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-violet-300 bg-violet-50 text-[10px] font-bold leading-none text-violet-700 sm:h-10 sm:w-10 sm:text-[11px]">
                    {accountInitials(account.accountName)}
                  </span>
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white shadow ring-2 ring-white transition-opacity group-hover:bg-rose-600">
                    <X className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                </button>
              ))}
              {selectedAccountOverflowCount > 0 ? (
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50 text-[10px] font-bold text-slate-600 sm:h-10 sm:w-10 sm:text-[11px]"
                  title={`${selectedAccountOverflowCount} more selected account${selectedAccountOverflowCount !== 1 ? "s" : ""}`}
                >
                  +{selectedAccountOverflowCount}
                </span>
              ) : null}
            </div>
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5">
              <button
                type="button"
                onClick={onGenerateClick}
                disabled={!onGenerateClick}
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] font-semibold leading-tight text-violet-700 transition-colors hover:bg-violet-100"
                title="Save selected accounts as a watchlist"
              >
                <Bookmark className="h-3 w-3 shrink-0" strokeWidth={2} />
                Save as watchlist
              </button>
              <button
                type="button"
                onClick={() => onHighlightedAccountIdsChange?.([])}
                className="px-1 py-0.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Section Detail Modal */}
      <SectionDetailModal
        isOpen={isSectionModalOpen}
        onClose={() => {
          setIsSectionModalOpen(false);
          setSelectedAccount(null);
          setSelectedSection(null);
        }}
        accountData={selectedAccount}
        sectionId={selectedSection}
        organizationName={organizationName}
        allAccounts={orderedSummaryData.filter(acc => acc.hasSelectedDateData)}
        allSections={enabledSections.map(s => s.id)} // overall is handled separately with index -1
        currentAccountIndex={currentAccountIndex}
        currentSectionIndex={currentSectionIndex}
        onNavigate={handleNavigate}
      />

      {/* Full Briefing Modal */}
      <FullBriefingModal
        isOpen={isFullBriefingModalOpen}
        onClose={() => {
          setIsFullBriefingModalOpen(false);
          setSelectedAccount(null);
        }}
        accountData={selectedAccount}
        sections={sections}
        selectedDate={selectedDate}
        organizationName={organizationName}
      />
    </div>
  );
};
